import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useMediaQuery } from "@react-hook/media-query";
import { EllipsisOutlined, LockOutlined, UnlockOutlined } from "@ant-design/icons";
import { Button, Dropdown, Modal, Pagination, Popover, Spin, Table, Tag, Tooltip } from "antd";
import type { ColumnsType, TableProps } from "antd/es/table";
import type { ProductItemIntegrationResponse } from "apiClients/base-generated";
import { useLocaleStore, type Currency } from "stores/localeStore";
import { formatCurrencyAmount, formatCurrencyAmountOrDash } from "utility/formatCurrency";
import {
  integrationProductActionMenuItems,
  isIntegrationProductAction,
  type IntegrationProductAction,
} from "../integrationProductActions";
import type {
  IntegrationLockConfirmPayload,
  IntegrationRowLockEntry,
  IntegrationRowLocks,
} from "../integrationRowLockTypes";
import { integrationProductRowKey } from "../integrationProductRowKey";
import {
  effectiveLockEntryForRow,
  formatLockUntilLabel,
  formatPausedStockLabel,
  isRowLockActive,
} from "../rowLockUtils";
import { IntegrationLockUntilModal } from "../modals/IntegrationLockUntilModal";
import {
  IntegrationBulkActionsBar,
  type IntegrationBulkActionsBarProps,
} from "./IntegrationBulkActionsToolbar";
import styles from "./IntegrationProductsTable.module.css";

export type { IntegrationProductAction };
export type { IntegrationRowLockEntry, IntegrationRowLocks };

const emptyNotLoading = (
  <div style={{ textAlign: "center", padding: "96px 0", fontSize: "0.95rem" }}>Inga produkter hittades</div>
);

export const rowKeyString = integrationProductRowKey;

function isFinitePrice(v: number | null | undefined): v is number {
  return v != null && Number.isFinite(v);
}

/** Kampanj lägre än ordinarie (`salePriceInVat`) → kampanjpris tydligt + genomstruket ordinarie. */
function renderUtPrisCell(record: ProductItemIntegrationResponse, currency: Currency): React.ReactNode {
  const sale = record.salePriceInVat;
  const campaign = record.campaignPriceInVat;
  const saleOk = isFinitePrice(sale);
  const campaignOk = isFinitePrice(campaign);

  if (campaignOk && saleOk && campaign < sale) {
    const campaignLabel = formatCurrencyAmount(campaign, currency);
    const ordinaryLabel = formatCurrencyAmount(sale, currency);
    return (
      <span className={styles.outletPriceCell}>
        <span className={styles.campaignPrice}>{campaignLabel}</span>
        <span className={styles.originalPrice} title={`Ordinarie pris ${ordinaryLabel}`}>
          {ordinaryLabel}
        </span>
      </span>
    );
  }

  if (campaignOk && (!saleOk || campaign >= sale)) {
    return formatCurrencyAmountOrDash(campaign, currency);
  }

  return formatCurrencyAmountOrDash(sale, currency);
}

function stockDisplay(v: number | null | undefined): React.ReactNode {
  if (v == null) return "—";
  return v;
}

function renderLagerCellWithStockPopover(record: ProductItemIntegrationResponse): React.ReactNode {
  const shelf = record.storeShelf?.trim();
  const displayShelf = shelf && shelf.length > 0 ? shelf : "—";
  const summaryNumeric =
    record.northmillLockedQuantity != null ? record.northmillLockedQuantity : record.storeStockCount;
  const summaryContent =
    summaryNumeric != null && Number.isFinite(summaryNumeric) ? (
      <Tag
        color={summaryNumeric === 0 ? "red" : "green"}
        style={{ margin: 0 }}
      >
        <span className={styles.stockPopoverDashUnderline}>{summaryNumeric}</span>
      </Tag>
    ) : (
      <span className={styles.stockPopoverDashUnderline}>—</span>
    );
  return (
    <Popover
      title="Lagerdetaljer"
      content={
        <div className={styles.stockPopoverRows}>
          <div className={styles.stockPopoverRow}>
            <span className={styles.stockPopoverRowLabel}>WebbLager</span>
            <span className={styles.stockPopoverRowValue}>{stockDisplay(record.webStockCount)}</span>
          </div>
          <div className={styles.stockPopoverRow}>
            <span className={styles.stockPopoverRowLabel}>ButiksLager</span>
            <span className={styles.stockPopoverRowValue}>{stockDisplay(record.storeStockCount)}</span>
          </div>
          <div className={styles.stockPopoverRow}>
            <span className={styles.stockPopoverRowLabel}>Northmill lager</span>
            <span className={styles.stockPopoverRowValue}>{stockDisplay(record.northmillLockedQuantity)}</span>
          </div>
          <div className={styles.stockPopoverRow}>
            <span className={styles.stockPopoverRowLabel}>Butikshylla</span>
            <span className={styles.stockPopoverRowValue}>{displayShelf}</span>
          </div>
        </div>
      }
      trigger="click"
      destroyTooltipOnHide
    >
      <span
        className={styles.storeStockPopoverTrigger}
        title="Klicka för att visa alla lager"
        role="button"
        tabIndex={0}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") e.stopPropagation();
        }}
      >
        {summaryContent}
      </span>
    </Popover>
  );
}

function findRecordForRowKey(
  data: ProductItemIntegrationResponse[],
  key: string,
): { record: ProductItemIntegrationResponse; index: number } | undefined {
  for (let i = 0; i < data.length; i++) {
    const r = data[i];
    if (rowKeyString(r, i) === key) {
      return { record: r, index: i };
    }
  }
  return undefined;
}

export type IntegrationProductsTablePaginationConfig = {
  /** 1-based page (Ant Design `Pagination`). */
  current: number;
  pageSize: number;
  total: number;
  onChange: (page: number, pageSize: number) => void;
};

export type IntegrationProductsTableProps = {
  dataSource: ProductItemIntegrationResponse[];
  /** Fotrad: ”Visar … av …” till vänster och `Pagination` till höger (samma rad som tidigare summering). */
  pagination?: IntegrationProductsTablePaginationConfig;
  loading?: boolean;
  rowSelection?: TableProps<ProductItemIntegrationResponse>["rowSelection"];
  /**
   * Radnyckel → lås (datum + ev. pausat lager). Saknad post = olåst.
   * Om utelämnad används intern state.
   */
  rowLocks?: IntegrationRowLocks;
  onRowAction?: (action: IntegrationProductAction, record: ProductItemIntegrationResponse, rowKey: string) => void;
  /** `entry` null = lås upp. Returnera Promise om lås ska vänta på API (modal stängs först vid lyckad `await`). */
  onRowLockChange?: (
    rowKey: string,
    entry: IntegrationRowLockEntry | null,
    record: ProductItemIntegrationResponse,
  ) => void | Promise<void>;
  /** Synk-paus / lås-knapp. Döljs t.ex. för listan ”Not synced” där lås inte är relevant. */
  showRowLockColumn?: boolean;
  /** T.ex. när föräldern kör Northmill-uppdateringen (synkpaus) för rad-modalen. */
  lockConfirmLoading?: boolean;
  /** Markeringsräknare + bulk-åtgärder i tabellens `title` (ovanför kolumnrubriker). */
  bulkActions?: Omit<IntegrationBulkActionsBarProps, "className">;
  /** Northmill-grupp (namn); visas som extra kolumn när satt. */
  northmillGroupLabel?: (record: ProductItemIntegrationResponse) => ReactNode;
  /** Northmill kontokod (namn); visas som extra kolumn när satt. */
  northmillAccountCodeLabel?: (record: ProductItemIntegrationResponse) => ReactNode;
};

export const IntegrationProductsTable: React.FC<IntegrationProductsTableProps> = ({
  dataSource,
  pagination: paginationConfig,
  loading = false,
  rowSelection,
  rowLocks: rowLocksProp,
  onRowAction,
  onRowLockChange,
  showRowLockColumn = true,
  lockConfirmLoading = false,
  bulkActions,
  northmillGroupLabel,
  northmillAccountCodeLabel,
}) => {
  const activeLocale = useLocaleStore((s) => s.activeLanguage?.locale);
  const currency = useLocaleStore((s) => s.currency);
  const showShelfAndVariantColumns = useMediaQuery("(min-width: 1350px)");
  const [internalLocks, setInternalLocks] = useState<IntegrationRowLocks>({});
  const rowLocks = rowLocksProp ?? internalLocks;

  const [lockModalKeys, setLockModalKeys] = useState<string[] | null>(null);

  const applyRowLock = useCallback(
    async (key: string, entry: IntegrationRowLockEntry | null) => {
      const found = findRecordForRowKey(dataSource, key);
      if (!found) return;
      await Promise.resolve(onRowLockChange?.(key, entry, found.record));
      if (rowLocksProp === undefined) {
        setInternalLocks((prev) => {
          const next = { ...prev };
          if (entry == null) {
            delete next[key];
          } else {
            next[key] = entry;
          }
          return next;
        });
      }
    },
    [dataSource, onRowLockChange, rowLocksProp],
  );

  const confirmLockModal = useCallback(
    async (payload: IntegrationLockConfirmPayload) => {
      const entry: IntegrationRowLockEntry = {
        lockedUntil: payload.lockedUntilIso,
        pausedStockQuantity: payload.pausedStockQuantity,
      };
      const keys = lockModalKeys ?? [];
      for (const key of keys) {
        await applyRowLock(key, entry);
      }
    },
    [lockModalKeys, applyRowLock],
  );

  const openLockModalForKeys = useCallback((keys: string[]) => {
    setLockModalKeys(keys.length ? keys : null);
  }, []);

  const toggleLockColumn = useCallback(
    (record: ProductItemIntegrationResponse, index: number) => {
      const key = rowKeyString(record, index);
      const lockEntry = effectiveLockEntryForRow(key, rowLocks, record);
      if (isRowLockActive(lockEntry)) {
        Modal.confirm({
          title: "Låsa upp synkpaus?",
          content:
            "Pausen tas bort och Northmill-integrationen synkar den här artikeln som vanligt igen.",
          okText: "Lås upp",
          cancelText: "Avbryt",
          onOk: () => applyRowLock(key, null),
        });
        return;
      }
      openLockModalForKeys([key]);
    },
    [rowLocks, applyRowLock, openLockModalForKeys],
  );

  const applyRowMenuAction = useCallback(
    (action: IntegrationProductAction, record: ProductItemIntegrationResponse, index: number) => {
      const key = rowKeyString(record, index);
      if (action === "lock") {
        openLockModalForKeys([key]);
        return;
      }
      if (action === "unlock") {
        void applyRowLock(key, null);
        return;
      }
      onRowAction?.(action, record, key);
    },
    [onRowAction, openLockModalForKeys, applyRowLock],
  );

  const rowClassName = useCallback(
    (record: ProductItemIntegrationResponse, index: number) => {
      const key = rowKeyString(record, index);
      const locked =
        showRowLockColumn && isRowLockActive(effectiveLockEntryForRow(key, rowLocks, record));
      return locked ? styles.rowLocked : "";
    },
    [rowLocks, showRowLockColumn],
  );

  const columns = useMemo<ColumnsType<ProductItemIntegrationResponse>>(
    () => [
      {
        title: "Produktnamn",
        dataIndex: "fullDisplayName",
        render: (v: string | null | undefined) => v ?? "—",
      },
      {
        title: "Art.nr",
        dataIndex: "itemNo",
        render: (v: string | null | undefined) => v ?? "—",
      },
      {
        title: "EAN",
        dataIndex: "ean",
        render: (v: string | null | undefined) => v ?? "—",
      },
      ...(northmillGroupLabel
        ? ([
            {
              title: "Grupp",
              key: "northmillGroup",
              render: (_: unknown, record: ProductItemIntegrationResponse) => northmillGroupLabel(record),
            },
          ] satisfies ColumnsType<ProductItemIntegrationResponse>)
        : []),
      ...(northmillAccountCodeLabel
        ? ([
            {
              title: "Kontokod",
              key: "northmillAccountCode",
              render: (_: unknown, record: ProductItemIntegrationResponse) => northmillAccountCodeLabel(record),
            },
          ] satisfies ColumnsType<ProductItemIntegrationResponse>)
        : []),
      {
        title: "UtPris",
        key: "utPris",
        align: "center" as const,
        render: (_, record) => renderUtPrisCell(record, currency),
      },
      {
        title: "Lager",
        key: "stockSummary",
        render: (_, record) => renderLagerCellWithStockPopover(record),
      },
      ...(showShelfAndVariantColumns
        ? ([
            {
              title: "Säljkanal Sverige",
              key: "hasSwedenSalesChannel",
              align: "center" as const,
              render: (_, record) => (
                <Tag color={record.hasSwedenSalesChannel ? "green" : "red"}>
                  {record.hasSwedenSalesChannel ? "Ja" : "Nej"}
                </Tag>
              ),
            },
            // {
            //   title: "VariantProdukt",
            //   key: "isVariantProduct",
            //   align: "center" as const,
            //   render: (_, record) => (
            //     <Tag color={record.isVariantProduct ? "green" : "default"}>
            //       {record.isVariantProduct ? "Ja" : "Nej"}
            //     </Tag>
            //   ),
            // },
          ] satisfies ColumnsType<ProductItemIntegrationResponse>)
        : []),
      ...(showRowLockColumn
        ? ([
            {
              title: "",
              key: "syncLock",
              width: 52,
              align: "center" as const,
              render: (_: unknown, record: ProductItemIntegrationResponse, index: number) => {
                const key = rowKeyString(record, index);
                const lockEntry = effectiveLockEntryForRow(key, rowLocks, record);
                const locked = isRowLockActive(lockEntry);
                const untilLabel = formatLockUntilLabel(lockEntry);
                const stockLabel = formatPausedStockLabel(lockEntry);
                const lockLines = [
                  locked && untilLabel ? `Paus t.o.m. ${untilLabel}` : null,
                  locked && stockLabel ? `Lager: ${stockLabel}` : null,
                ].filter(Boolean);
                return (
                  <Tooltip
                    title={
                      locked
                        ? `${lockLines.join(" · ")} — klicka för att låsa upp nu`
                        : "Lås — välj datum och ev. pausat lager (stoppar kvantitetssynk)"
                    }
                  >
                    <Button
                      type="text"
                      size="small"
                      icon={
                        locked ? (
                          <LockOutlined style={{ color: "#fa8c16" }} />
                        ) : (
                          <UnlockOutlined style={{ color: "#52c41a" }} />
                        )
                      }
                      aria-label={locked ? "Lås upp rad" : "Lås rad för synk"}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleLockColumn(record, index);
                      }}
                    />
                  </Tooltip>
                );
              },
            },
          ] satisfies ColumnsType<ProductItemIntegrationResponse>)
        : []),
      {
        title: "",
        key: "fastActions",
        width: 52,
        align: "center",
        render: (_, record, index) => (
          <Dropdown
            menu={{
              items: integrationProductActionMenuItems,
              onClick: ({ key, domEvent }) => {
                domEvent.stopPropagation();
                if (typeof key === "string" && isIntegrationProductAction(key)) {
                  applyRowMenuAction(key, record, index);
                }
              },
            }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              icon={<EllipsisOutlined />}
              aria-label="Snabbåtgärder"
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        ),
      },
    ],
    [
      activeLocale,
      currency,
      rowLocks,
      applyRowMenuAction,
      toggleLockColumn,
      showShelfAndVariantColumns,
      showRowLockColumn,
      northmillGroupLabel,
      northmillAccountCodeLabel,
    ],
  );

  return (
    <>
      <IntegrationLockUntilModal
        open={lockModalKeys != null && lockModalKeys.length > 0}
        onCancel={() => setLockModalKeys(null)}
        onConfirm={confirmLockModal}
        confirmLoading={lockConfirmLoading}
        title={
          lockModalKeys && lockModalKeys.length > 1 ? "Pausa markerade rader" : "Pausa synk & lager"
        }
      />
      <Spin spinning={loading}>
        <Table<ProductItemIntegrationResponse>
          size="middle"
          rowKey={rowKeyString}
          rowClassName={rowClassName}
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          rowSelection={rowSelection}
          title={
            bulkActions
              ? () => (
                  <IntegrationBulkActionsBar {...bulkActions} className={styles.bulkActionsBar} />
                )
              : undefined
          }
          footer={
            paginationConfig
              ? () => (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 12,
                      padding: "4px 0 0",
                    }}
                  >
                    <div style={{ color: "rgba(0, 0, 0, 0.45)", fontSize: 13 }}>
                      Visar {dataSource.length} av {paginationConfig.total}
                    </div>
                    <div className={styles.tableFooterPagination}>
                      <Pagination
                        size="small"
                        current={paginationConfig.current}
                        pageSize={paginationConfig.pageSize}
                        total={paginationConfig.total}
                        onChange={paginationConfig.onChange}
                        showSizeChanger
                        pageSizeOptions={[10, 20, 50, 100]}
                      />
                    </div>
                  </div>
                )
              : undefined
          }
          locale={{
            emptyText: loading ? "Laddar…" : emptyNotLoading,
          }}
        />
      </Spin>
    </>
  );
};
